// Assistants section: the two assistants (Bro EFD, Bro PPI — one per data
// model, src/data_agent/assistants.py) and the data set each reads on this
// instance by default (users can get another one in their own settings).
// Only data sets of the assistant's model can be chosen; the server enforces
// it too. "Check data" proves the data set holds what the assistant reads.

import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStethoscope, faArrowsRotate, faCircleInfo, faDatabase, faChartPie, faBuildingColumns } from '@fortawesome/free-solid-svg-icons';
import { StatusIcon, CheckRows, Spinner } from './ui';
import { useConsole } from './context';

export const variantLabel = (meta, key) => meta.variants?.find((v) => v.key === key)?.label || key || 'not set';
const modelLabel = (meta, key) => meta.data_models?.find((m) => m.key === key)?.label || (key || '').toUpperCase();

function AssistantCard({ assistant, sources, canEdit, flash, onChanged }) {
  const { client, meta } = useConsole();
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState('');
  const bound = assistant.binding?.id || '';
  const env = assistant.env || {};
  const fitting = sources.filter((s) => s.model === assistant.model);
  const model = meta.data_models?.find((m) => m.key === assistant.model);

  const bind = async (value) => {
    setBusy('bind');
    try {
      await client.put(`/api/admin/assistants/${assistant.key}`, { data_source_id: value ? Number(value) : null });
      flash('success', `${assistant.label} now reads ${value ? fitting.find((s) => s.id === Number(value))?.name : 'the environment variables'}.`);
      setCheck(null);
      onChanged();
    } catch (e) { flash('error', e.message); }
    setBusy('');
  };

  const runCheck = async () => {
    setBusy('check');
    setCheck(null);
    try { setCheck(await client.post(`/api/admin/assistants/${assistant.key}/check`, {})); } catch (e) { setCheck({ status: 'fail', error: e.message }); }
    setBusy('');
  };

  const scoped = new Set(assistant.relations);
  return (
    <div className="um-assistant">
      <div className="um-assistant-head">
        <div className="um-assistant-title">
          <span className={`um-assistant-icon ${assistant.model}`}>
            <FontAwesomeIcon icon={assistant.model === 'ppi' ? faChartPie : faBuildingColumns} />
          </span>
          <div>
            <h4 className="um-block-title">{assistant.label}</h4>
            <div className="um-hint">Reads {modelLabel(meta, assistant.model)} data sets only — {model?.description}</div>
          </div>
        </div>
        <button type="button" className="um-btn um-btn-ghost" onClick={runCheck} disabled={!!busy}>
          {busy === 'check' ? <Spinner small /> : <FontAwesomeIcon icon={faStethoscope} />} Check data
        </button>
      </div>
      <div className="um-assistant-bind">
        <label className="um-label"><FontAwesomeIcon icon={faDatabase} /> Data set on this instance (default for every user)</label>
        <select value={bound} disabled={!canEdit || busy === 'bind'} onChange={(e) => bind(e.target.value)}>
          <option value="">No data set — server configuration ({env.db_name || '—'}{env.has_password ? '' : ', no password set'})</option>
          {fitting.map((s) => (
            <option key={s.id} value={s.id}>{s.name}{variantLabel(meta, s.variant) !== 'Standard' ? ` — variant ${variantLabel(meta, s.variant)}` : ''}</option>
          ))}
        </select>
        {fitting.length === 0 && (
          <div className="um-hint">No {modelLabel(meta, assistant.model)} data set yet — create one under Data sets.</div>
        )}
        {assistant.binding && (
          <div className="um-hint">Variant: {variantLabel(meta, assistant.binding.variant)} — decides how the assistant reads this data.</div>
        )}
        {!canEdit && <div className="um-hint">Only a super admin changes this.</div>}
      </div>
      {check && (
        <div className={`um-check-card ${check.status}`}>
          <div className="um-check-card-head">
            <StatusIcon status={check.status} />
            <strong>{check.status === 'ok' ? 'Everything the assistant reads is there' : check.status === 'warn' ? 'Usable — with gaps' : 'Not usable'}</strong>
            {check.source && <span className="um-muted">via {check.source.name}</span>}
          </div>
          {check.error && <div className="um-hint error">{check.error}</div>}
          <CheckRows rows={(check.relations || []).map((r) => ({
            ...r, name: `${r.name}${scoped.has(r.name) ? '' : ' (aggregate)'}`,
          }))} />
        </div>
      )}
    </div>
  );
}

export default function AssistantsSection({ flash }) {
  const { client, meta } = useConsole();
  const [assistants, setAssistants] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [a, s] = await Promise.all([client.get('/api/admin/assistants'), client.get('/api/admin/data-sources')]);
      setAssistants(a);
      setSources(s);
    } catch (e) { setError(e.message); }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="um-section-bar">
        <div className="um-subtitle">One assistant per data model. Here: which data set each reads on this instance — a user can get another one in their settings.</div>
        <div className="um-topbar-right">
          <button type="button" className="um-icon-btn" onClick={load} title="Reload"><FontAwesomeIcon icon={faArrowsRotate} /></button>
        </div>
      </div>
      <div className="um-note"><FontAwesomeIcon icon={faCircleInfo} /> Per request: the user&apos;s own data set → the data set here → the server configuration. Bro EFD only docks onto EFD data sets, Bro PPI only onto PPI data sets.</div>
      {error && <div className="um-hint error">{error}</div>}
      {!assistants ? <div className="um-loading"><Spinner /></div> : (
        <div className="um-assistant-list">
          {assistants.map((a) => (
            <AssistantCard key={a.key} assistant={a} sources={sources} canEdit={meta.permissions.manage_instance}
              flash={flash} onChanged={load} />
          ))}
        </div>
      )}
    </>
  );
}
